import React from "react";
import Navbar from "../../components/Navbar";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../../Images";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  CloudUpload,
  ChevronUp,
  ChevronDown,
  BookmarkX,
  Eye,
  Search,
  Pencil,
  CloudDownload,
  Trash2,
  MoreVertical,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  Filter,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ViewBrandModal from "../../components/Brands/ViewBrandModal";
import DeleteModal from "../../components/Brands/DeleteModal";
import DeactivateModal from "../../components/Brands/DeactivateModal";
import Config from "../../Config";
import axios from "axios";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

const Brands = () => {
  const navigate = useNavigate();

  // API related states
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search state
  const [globalFilter, setGlobalFilter] = useState("");

  // Modal states
  const [viewModal, setViewModal] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [isExportOpen, setIsExportOpen] = useState(false);
  const exportRef = useRef(null);
  const datePickerRef = useRef(null);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setIsExportOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Clear date range
  const clearDateRange = (e) => {
    e?.stopPropagation();
    setDateRange({ start: '', end: '' });
    setIsDatePickerOpen(false);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setDateRange({ start: '', end: '' });
    setFilterStatus('');
    setIsDatePickerOpen(false);
  };

  const hasActiveFilters = dateRange.start || dateRange.end || filterStatus;

  // Handle date range change
  const handleDateRangeChange = (type, value) => {
    setDateRange(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // Quick date range selections
  const quickRanges = [
    {
      label: 'Last 7 days',
      action: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 7);
        setDateRange({
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        });
      }
    },
    {
      label: 'Last 30 days',
      action: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        setDateRange({
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        });
      }
    },
    {
      label: 'This month',
      action: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        setDateRange({
          start: start.toISOString().split('T')[0],
          end: now.toISOString().split('T')[0]
        });
      }
    }
  ];

  const transformApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      console.warn("Invalid API data structure:", apiData);
      return [];
    }

    return apiData.map((vendor, index) => ({
      id: vendor.id?.toString() || `${index + 1}`.padStart(3, "0"),
      brandName: vendor.brand_name || "N/A",
      email: vendor.email || "N/A",
      phone: vendor.phone_number || "N/A",
      category: vendor.brand_category || "Uncategorized",
      location: vendor.location || "N/A",
      website: vendor.website || "",
      facebook: vendor.facebook || "",
      instagram: vendor.instagram || "",
      twitter: vendor.twitter || "",
      vendorSlug: vendor.vendor_slug || "",
      onboardType: vendor.onboard_type || "admin",
      emailVerifiedAt: vendor.email_verified_at,
      phoneVerifiedAt: vendor.phone_number_verified_at,
      deletedAt: vendor.deleted_at,
      created_at: vendor.created_at, // Keep original date
      dateCreated: vendor.created_at
        ? new Date(vendor.created_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        : "N/A",
      status: vendor.deleted_at
        ? "Inactive"
        : vendor.email_verified_at || vendor.phone_verified_at
        ? "Active"
        : "Pending Verification",
    }));
  };

  const sortBrandsNewestFirst = (rows) => {
    const parseTime = (row) => {
      if (!row.created_at) return null;
      const ms = new Date(row.created_at).getTime();
      return Number.isNaN(ms) ? null : ms;
    };
    return [...rows].sort((a, b) => {
      const tb = parseTime(b);
      const ta = parseTime(a);
      if (tb != null && ta != null && tb !== ta) return tb - ta;
      if (tb != null && ta == null) return -1;
      if (tb == null && ta != null) return 1;
      const nb = Number(b.id);
      const na = Number(a.id);
      if (!Number.isNaN(nb) && !Number.isNaN(na)) return nb - na;
      return String(b.id).localeCompare(String(a.id), undefined, { numeric: true });
    });
  };

  useEffect(() => {
    const API_ENDPOINTS = {
      getAllVendors: `${Config.baseUrl}/all/vendors`,
    };
    const fetchBrands = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_ENDPOINTS.getAllVendors, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const data = await response.data;
        if (data?.data?.status_code === "000") {
          const rows = transformApiData(data.data.data);
          setBrands(sortBrandsNewestFirst(rows));
        }
      } catch (error) {
        setError("Failed to fetch brands data");
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);
  const [rowSelection, setRowSelection] = useState({});

  // Apply date range and additional filters to get data for table
  // created_at format from API: 2025-02-12T16:25:13.000000Z (UTC)
  const filteredBrands = useMemo(() => {
    let result = brands;
    // Date range filter (compare in UTC so YYYY-MM-DD range matches created_at)
    if (dateRange.start && dateRange.end) {
      const startDate = new Date(dateRange.start + 'T00:00:00.000Z');
      const endDate = new Date(dateRange.end + 'T23:59:59.999Z');
      result = result.filter((row) => {
        const raw = row.created_at;
        if (!raw) return false;
        const rowDate = new Date(raw);
        if (Number.isNaN(rowDate.getTime())) return false;
        return rowDate >= startDate && rowDate <= endDate;
      });
    }
    // Status filter
    if (filterStatus) {
      result = result.filter((row) => row.status === filterStatus);
    }
    return result;
  }, [brands, dateRange.start, dateRange.end, filterStatus]);

  //Todo => Table columns definition
  const columns = useMemo(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <div className="px-1">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
              className="rounded border-gray-300 text-[#FF6C6C] focus:ring-[#FF6C6C]/20
                     cursor-pointer transition-all checked:bg-[#FF6C6C] checked:hover:bg-[#FF6C6C]/90"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              className="rounded border-gray-300 text-[#FF6C6C] focus:ring-[#FF6C6C]/20
                     cursor-pointer transition-all checked:bg-[#FF6C6C] checked:hover:bg-[#FF6C6C]/90"
            />
          </div>
        ),
        enableSorting: false,
        enableGlobalFilter: false,
      },
      {
        accessorKey: "id",
        header: "ID",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "brandName",
        header: "Brand Name",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "phone",
        header: "Phone",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "location",
        header: "Location",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "dateCreated",
        header: "Date",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => (
          <span className={getStatusStyle(info.getValue())}>
            {info.getValue()}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <ActionMenu
            brand={row.original}
            onView={() => {
              setSelectedBrand(row.original);
              setViewModal(true);
            }}
            onDeactivate={() => {
              setSelectedBrand(row.original);
              setDeactivateModal(true);
            }}
            onDelete={() => {
              setSelectedBrand(row.original);
              setDeleteModal(true);
            }}
          />
        ),
        enableSorting: false,
        enableGlobalFilter: false,
      },
    ],
    []
  );

  //* Initialize table instance
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const table = useReactTable({
    data: filteredBrands,
    columns,
    state: {
      rowSelection,
      globalFilter,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newState = updater({
          pageIndex,
          pageSize,
        });
        setPageIndex(newState.pageIndex);
        setPageSize(newState.pageSize);
      } else {
        setPageIndex(updater.pageIndex);
        setPageSize(updater.pageSize);
      }
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: false,
  });

  // Reset pagination when data or filters change
  useEffect(() => {
    setPageIndex(0);
  }, [brands, filteredBrands.length, dateRange.start, dateRange.end, filterStatus]);

  //* Helper functions
  const getStatusStyle = (status) => {
    const baseStyle = "px-3 py-1 rounded-full text-xs font-medium";
    switch (status.toLowerCase()) {
      case "active":
        return `${baseStyle} bg-green-100 text-green-800`;
      case "inactive":
        return `${baseStyle} bg-red-100 text-red-800`;
      case "pending verification":
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800`;
    }
  };

  //* Export functionality
  const handleExport = (scope = 'filtered') => {
    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return '""';
      const stringValue = String(value);
      if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = [
      "ID",
      "Brand Name",
      "Email",
      "Phone",
      "Industry",
      "Location",
      "Date Created",
      "Status"
    ];

    // Export filtered (current view: date + status + search) or all
    const rowsToExport = scope === 'all'
      ? brands.map((row) => ({ original: row }))
      : table.getFilteredRowModel().rows;

    const csvRows = [
      headers.join(','),
      ...rowsToExport.map((row) => {
        const o = row.original;
        const values = [
          o.id,
          o.brandName,
          o.email,
          o.phone,
          o.category,
          o.location,
          o.dateCreated,
          o.status,
        ];
        return values.map(escapeCsvValue).join(',');
      }),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const dateRangeStr = dateRange.start && dateRange.end && scope === 'filtered'
      ? `_${formatDate(dateRange.start).replace(/,/g, '')}_to_${formatDate(dateRange.end).replace(/,/g, '')}`
      : '';
    const scopeStr = scope === 'all' ? '_all' : '';
    link.setAttribute("download", `brands_export${scopeStr}${dateRangeStr}_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setIsExportOpen(false);
  };

  //* Action Menu Component
  const ActionMenu = ({ brand, onView, onDeactivate, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [coordinates, setCoordinates] = useState({ top: 10, left: 0 });
    const buttonRef = useRef(null);

    const handleClick = (e) => {
      e.stopPropagation();
      if (!isOpen) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 150;
        const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

        setCoordinates({
          top: showAbove ? rect.top - dropdownHeight : rect.bottom,
          left: rect.left - 180,
        });
      }
      setIsOpen(!isOpen);
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (buttonRef.current && !buttonRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('click', handleClickOutside);
      }

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, [isOpen]);

    return (
      <div className="relative flex items-center justify-center">
        <button
          ref={buttonRef}
          className="p-1.5 rounded-full hover:bg-gray-100 flex items-center justify-center"
          onClick={handleClick}
        >
          <MoreVertical size={16} />
        </button>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed z-[9999] w-48 overflow-hidden bg-white border shadow-lg rounded-xl"
            style={{
              top: `${coordinates.top}px`,
              left: `${coordinates.left}px`,
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView();
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-3 space-x-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Eye size={14} className="text-orange-500" />
              <span>View Brand Info</span>
            </button>
            <button
              className="flex items-center w-full px-4 py-3 space-x-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/edit-brand/${brand.id}`);
                setIsOpen(false);
              }}
            >
              <Pencil size={14} className="text-blue-500" />
              <span>Edit Brand Info</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeactivate();
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-3 space-x-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <BookmarkX size={14} className="text-red-500" />
              <span>De-Activate</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-3 space-x-2 text-sm text-red-600 hover:bg-gray-50"
            >
              <Trash2 size={14} className="text-red-500" />
              <span>Delete</span>
            </button>
          </motion.div>
        )}
      </div>
    );
  };

  // Error component
  const ErrorState = ({ error, onRetry }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-12"
    >
      <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Failed to Load Brands
      </h3>
      <p className="text-gray-600 text-center mb-4 max-w-md">{error}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF5555] transition-colors"
      >
        Try Again
      </button>
    </motion.div>
  );

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="flex space-x-4 py-4">
            <div className="w-6 h-6 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <Navbar page="Brands" />
      <div className="min-h-screen bg-white">
        {/* HEADER SECTION */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative bg-gradient-to-r from-[#f97a69] to-[#FF8F8F] text-white overflow-hidden"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.07 }}
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          <div className="relative px-6 py-4 mx-auto max-w-7xl sm:px-8 lg:px-12">
            <div className="flex flex-col items-center space-y-8 md:flex-row md:space-x-12 md:space-y-0">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                  duration: 0.8,
                }}
                className="p-4 shadow-lg bg-white/10 rounded-3xl backdrop-blur-lg"
              >
                <img
                  src={Images.brandLogo}
                  alt="brands"
                  className="object-contain transition-transform duration-300 w-36 h-36 hover:scale-105"
                />
              </motion.div>

              <div className="space-y-4 text-center md:text-left">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-2"
                >
                  <h1 className="text-4xl font-bold tracking-tight">
                    Brands Management
                  </h1>
                  <p className="max-w-md text-lg text-white/90">
                    Create and manage Brands with this intuitive Feature
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-white/80">
                    <span>Total Brands: {brands.length}</span>
                    <span>•</span>
                    <span>
                      Active:{" "}
                      {brands.filter((b) => b.status === "Active").length}
                    </span>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center justify-center space-x-4 md:justify-start"
                >
                  <button
                    onClick={() => navigate("/create-brand")}
                    className="px-6 py-2.5 bg-white text-[#FF6C6C] rounded-full font-medium hover:bg-opacity-90 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <CloudUpload size={18} />
                    <span>Enroll Brand</span>
                  </button>
                  {/* <button
                    onClick={() => {}}
                    disabled={refreshing}
                    className="px-6 py-2.5 bg-white/20 text-white rounded-full font-medium hover:bg-white/30 transition-colors duration-200 flex items-center space-x-2 backdrop-blur-sm"
                  >
                    <RefreshCw
                      size={18}
                      className={refreshing ? "animate-spin" : ""}
                    />
                    <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
                  </button> */}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* DATA TABLE SECTION */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="px-6 py-8"
        >
          {/* Controls - single row: search, date, status, clear, export */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-shrink-0 w-full sm:w-64 min-w-0">
              <input
                type="text"
                placeholder="Search brands..."
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none"
              />
              <Search
                size={18}
                className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
              />
            </div>

            <div className="relative flex-shrink-0" ref={datePickerRef}>
              <button
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                className="px-4 py-2 border border-gray-300 rounded-xl flex items-center gap-2 hover:border-[#FF6C6C] transition-all duration-200 whitespace-nowrap"
              >
                <Calendar size={18} className="text-gray-400 shrink-0" />
                <span className="text-gray-600 text-sm">
                  {dateRange.start && dateRange.end
                    ? `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`
                    : "Date range"}
                </span>
                {dateRange.start && dateRange.end && (
                  <X
                    size={16}
                    className="text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearDateRange(e);
                    }}
                  />
                )}
              </button>

              <AnimatePresence>
                {isDatePickerOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute z-50 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-4 w-80"
                  >
                    <div className="space-y-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-medium text-gray-700">Start Date</label>
                          <input
                            type="date"
                            value={dateRange.start}
                            max={dateRange.end || undefined}
                            onChange={(e) => handleDateRangeChange('start', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200"
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-medium text-gray-700">End Date</label>
                          <input
                            type="date"
                            value={dateRange.end}
                            min={dateRange.start || undefined}
                            onChange={(e) => handleDateRangeChange('end', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200"
                          />
                        </div>
                      </div>

                      <div className="border-t border-gray-200 pt-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">Quick Select</p>
                        <div className="flex flex-col gap-2">
                          {quickRanges.map((range, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                range.action();
                                setIsDatePickerOpen(false);
                              }}
                              className="text-left text-sm px-3 py-2 hover:bg-gray-50 rounded-lg text-gray-600 transition-colors duration-200"
                            >
                              {range.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                        <button
                          onClick={clearDateRange}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                        >
                          Clear
                        </button>
                        <button
                          onClick={() => setIsDatePickerOpen(false)}
                          className="px-3 py-1.5 text-sm bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF5555] transition-colors duration-200"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Filter size={18} className="text-gray-400 hidden sm:block" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 min-w-[130px]"
              >
                <option value="">All statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Pending Verification">Pending Verification</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors duration-200 flex items-center gap-1 flex-shrink-0"
              >
                <X size={14} />
                Clear filters
              </button>
            )}

            <div className="flex items-center gap-2 flex-shrink-0 ml-auto" ref={exportRef}>
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsExportOpen(!isExportOpen)}
                  className="flex-1 sm:flex-none py-2 px-4 sm:px-6 text-gray-600 flex items-center justify-center gap-2 transition-colors duration-200 border border-gray-200 rounded-xl hover:bg-purple-50 hover:text-purple-600"
                >
                  <CloudDownload className="w-5 h-5" />
                  <span>Export</span>
                  <ChevronDown size={16} className={isExportOpen ? 'rotate-180' : ''} />
                </motion.button>
                <AnimatePresence>
                  {isExportOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="absolute right-0 z-50 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-1"
                    >
                      <button
                        onClick={() => handleExport('filtered')}
                        className="flex items-center w-full px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50"
                      >
                        Export filtered (current view)
                      </button>
                      <button
                        onClick={() => handleExport('all')}
                        className="flex items-center w-full px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50"
                      >
                        Export all
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden bg-white border rounded-xl">
            {error ? (
              <ErrorState error={error} onRetry={() => {}} />
            ) : loading ? (
              <div className="p-6">
                <LoadingSkeleton />
              </div>
            ) : brands.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Brands Found
                </h3>
                <p className="text-gray-600 text-center mb-4 px-4">
                  {globalFilter
                    ? "No brands match your search criteria."
                    : "Get started by enrolling your first brand."}
                </p>
                <button
                  onClick={() => navigate("/create-brand")}
                  className="px-4 py-2 bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF5555] transition-colors"
                >
                  Enroll Brand
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                {...{
                                  className: header.column.getCanSort()
                                    ? "cursor-pointer select-none flex items-center"
                                    : "",
                                  onClick:
                                    header.column.getToggleSortingHandler(),
                                }}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {{
                                  asc: <ChevronUp className="w-4 h-4 ml-2" />,
                                  desc: (
                                    <ChevronDown className="w-4 h-4 ml-2" />
                                  ),
                                }[header.column.getIsSorted()] ?? null}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {table.getRowModel().rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-6 py-12 text-center text-sm text-gray-500"
                        >
                          {hasActiveFilters || globalFilter
                            ? "No brands match your current filters or search. Try adjusting them."
                            : "No brands to display."}
                        </td>
                      </tr>
                    ) : (
                      table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {filteredBrands.length > 0 && (
              <div className="px-4 sm:px-6 py-4 bg-white border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent flex items-center gap-1 text-gray-700 text-sm font-medium transition-colors duration-200"
                      onClick={() => setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <ChevronLeft className="w-4 h-4 -ml-3" />
                    </button>
                    <button
                      className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent flex items-center gap-1 text-gray-700 text-sm font-medium transition-colors duration-200"
                      onClick={() => setPageIndex(old => Math.max(0, old - 1))}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Previous</span>
                    </button>
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600 whitespace-nowrap">Page</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {pageIndex + 1}
                      </span>
                      <span className="text-sm text-gray-600">of</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {table.getPageCount() || 1}
                      </span>
                    </div>
                    <button
                      className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent flex items-center gap-1 text-gray-700 text-sm font-medium transition-colors duration-200"
                      onClick={() => setPageIndex(old => Math.min(table.getPageCount() - 1, old + 1))}
                      disabled={!table.getCanNextPage()}
                    >
                      <span>Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent flex items-center gap-1 text-gray-700 text-sm font-medium transition-colors duration-200"
                      onClick={() => setPageIndex(table.getPageCount() - 1)}
                      disabled={!table.getCanNextPage()}
                    >
                      <ChevronRight className="w-4 h-4" />
                      <ChevronRight className="w-4 h-4 -ml-3" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-sm text-gray-600 whitespace-nowrap">Rows per page</span>
                    <select
                      value={pageSize}
                      onChange={e => {
                        const newSize = Number(e.target.value);
                        setPageSize(newSize);
                        setPageIndex(0);
                      }}
                      className="p-2 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] min-w-[70px]"
                    >
                      {[10, 20, 30, 40, 50].map(size => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      <ViewBrandModal
        isOpen={viewModal}
        onClose={() => setViewModal(false)}
        brand={selectedBrand}
      />

      <DeactivateModal
        isOpen={deactivateModal}
        onClose={() => setDeactivateModal(false)}
        userDetails={selectedBrand}
        statusChangeModalOpen={deactivateModal}
        statusChangeType="disable"
        onStatusChangeComplete={() => {
          setDeactivateModal(false);
        }}
      />

      <DeleteModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        brandName={selectedBrand?.brandName}
        onDelete={() => {
          setDeleteModal(false);
        }}
      />
    </div>
  );
};

export default Brands;
